import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Smartphone, Brain, Palette, TrendingUp, Share2, ShoppingCart, Megaphone, ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CursorGlow from '../components/CursorGlow';
import ScrollProgress from '../components/ScrollProgress';

const Services = () => {
  const [activeTab, setActiveTab] = useState('all');

  const services = [
    {
      id: 1,
      icon: Smartphone,
      title: 'Mobil Uygulama Geliştirme',
      shortDesc: 'iOS & Android için performanslı mobil uygulamalar',
      description: 'React Native veya Native teknolojiler ile kullanıcı dostu, ölçeklenebilir mobil uygulamalar geliştiriyoruz.',
      features: ['iOS & Android', 'React Native', 'Native Development', 'App Store Yayınlama'],
      path: '/services/mobile-app-development',
      category: 'development',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      id: 2,
      icon: Brain,
      title: 'Yapay Zeka Entegrasyonu',
      shortDesc: 'AI destekli akıllı çözümler',
      description: 'Yapay zeka ve makine öğrenmesi ile ürünlerinize akıllı özellikler kazandırıyoruz.',
      features: ['ChatGPT Entegrasyonu', 'Özel AI Modelleri', 'Veri Analizi', 'Otomasyon'],
      path: '/services/ai-integration',
      category: 'development',
      color: 'from-purple-500 to-pink-500'
    },
    {
      id: 3,
      icon: Palette,
      title: 'Web & Arayüz Tasarımı',
      shortDesc: 'Kullanıcı odaklı modern tasarımlar',
      description: 'UI/UX prensipleri ile kullanıcı deneyimini merkeze alan web siteleri ve arayüzler tasarlıyoruz.',
      features: ['UI/UX Tasarım', 'Responsive Web', 'Figma/Adobe XD', 'Marka Kimliği'],
      path: '/services/web-design',
      category: 'design',
      color: 'from-orange-500 to-red-500'
    },
    {
      id: 4,
      icon: TrendingUp,
      title: 'SEO & Dijital Büyüme',
      shortDesc: 'Organik trafik ve görünürlük artışı',
      description: 'SEO optimizasyonu ve dijital pazarlama stratejileri ile online varlığınızı büyütüyoruz.',
      features: ['SEO Optimizasyonu', 'Google Ads', 'İçerik Stratejisi', 'Analytics'],
      path: '/services/digital-growth',
      category: 'marketing',
      color: 'from-green-500 to-emerald-500'
    },
    {
      id: 5,
      icon: Share2,
      title: 'Sosyal Medya Yönetimi',
      shortDesc: 'Marka bilinirliği ve etkileşim',
      description: 'Sosyal medya stratejisi, içerik üretimi ve topluluk yönetimi ile markanızı güçlendiriyoruz.',
      features: ['İçerik Üretimi', 'Topluluk Yönetimi', 'Reklam Kampanyaları', 'Influencer Marketing'],
      path: '/services/social-media',
      category: 'marketing',
      color: 'from-pink-500 to-rose-500'
    },
    {
      id: 6,
      icon: ShoppingCart,
      title: 'E-Ticaret Sistemleri',
      shortDesc: 'Online satış ve ödeme altyapıları',
      description: 'Güvenli, hızlı ve kullanıcı dostu e-ticaret platformları ile online satışlarınızı artırıyoruz.',
      features: ['E-Ticaret Sitesi', 'Ödeme Entegrasyonu', 'Stok Yönetimi', 'Kargo Entegrasyonu'],
      path: '/services/ecommerce',
      category: 'development',
      color: 'from-yellow-500 to-orange-500'
    },
    {
      id: 7,
      icon: Megaphone,
      title: 'Medya Dağıtım & Reklam',
      shortDesc: 'Dijital reklam ve içerik dağıtımı',
      description: 'Google Ads, Meta Ads, video prodüksiyon ve AI destekli kreatif çözümler ile markanızın erişimini maksimize edin.',
      features: ['Google & Meta Ads', 'Video Prodüksiyon', 'AI Kreatif', 'Performans Analizi'],
      path: '/services/media-distribution',
      category: 'marketing',
      color: 'from-pink-500 to-rose-500'
    }
  ];

  const categories = [
    { id: 'all', name: 'Tümü', count: services.length },
    { id: 'development', name: 'Yazılım Geliştirme', count: services.filter(s => s.category === 'development').length },
    { id: 'design', name: 'Tasarım', count: services.filter(s => s.category === 'design').length },
    { id: 'marketing', name: 'Dijital Pazarlama', count: services.filter(s => s.category === 'marketing').length }
  ];

  const filteredServices = activeTab === 'all' 
    ? services 
    : services.filter(s => s.category === activeTab);

  return (
    <div className="min-h-screen bg-slate-900">
      <CursorGlow />
      <ScrollProgress />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute w-[600px] h-[600px] bg-cyan-500/20 rounded-full blur-3xl top-0 right-1/4 animate-pulse"></div>
          <div className="absolute w-[500px] h-[500px] bg-teal-500/20 rounded-full blur-3xl bottom-0 left-1/4 animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Dijital Dönüşüm
            <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent"> Hizmetlerimiz</span>
          </h1>

          <p className="text-xl text-gray-400 mb-12 max-w-3xl mx-auto">
            Mobil uygulamalardan yapay zeka entegrasyonuna, web tasarımından dijital pazarlamaya 
            kadar geniş yelpazede profesyonel çözümler sunuyoruz.
          </p>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveTab(category.id)}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  activeTab === category.id
                    ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white shadow-lg shadow-cyan-500/50'
                    : 'bg-slate-800/40 text-gray-400 hover:text-white border border-slate-700/50 hover:border-cyan-500/50'
                }`}
              >
                {category.name} ({category.count})
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredServices.map((service, index) => (
              <div
                key={service.id}
                className="group bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 hover:border-cyan-500/50 transition-all duration-500 hover:transform hover:-translate-y-2"
                style={{
                  animation: 'fade-in-up 0.8s ease-out both',
                  animationDelay: `${index * 0.1}s`
                }}
              >
                {/* Icon */}
                <div className="relative w-16 h-16 mb-6">
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center transition-transform duration-500 group-hover:rotate-12 group-hover:scale-110`}>
                    <service.icon className="w-8 h-8 text-white transition-transform duration-500 group-hover:rotate-[-12deg]" />
                  </div>
                  <div className="absolute inset-0 bg-cyan-500/50 rounded-xl blur-xl opacity-0 group-hover:opacity-70 transition-opacity duration-500"></div>
                </div>

                <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors">
                  {service.title}
                </h3>

                <p className="text-gray-400 mb-6">
                  {service.description}
                </p>

                {/* Features */}
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-400">
                      <Check className="w-4 h-4 text-cyan-400 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Link to={service.path}>
                  <Button className="w-full bg-slate-700/50 hover:bg-gradient-to-r hover:from-cyan-500 hover:to-teal-500 text-white group/btn">
                    Detaylı Bilgi
                    <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute w-[800px] h-[800px] bg-cyan-500/10 rounded-full blur-3xl top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Hangi Hizmet Size Uygun?
          </h2>
          <p className="text-xl text-gray-400 mb-8">
            Projeniz için en uygun çözümü birlikte belirleyelim. Ücretsiz danışmanlık için iletişime geçin.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/iletisim">
              <Button className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-8 py-6 text-lg">
                Ücretsiz Danışmanlık
              </Button>
            </Link>
            <Link to="/web-analiz">
              <Button variant="outline" className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 px-8 py-6 text-lg">
                Ücretsiz Analiz
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;
